import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useWatch } from 'react-hook-form';
import {
  domainOrResourceMapping,
  QAsyncMultiSelect,
  QAsyncMultiSelectProps,
  QBadge,
  QFlex,
  QFormControl,
  QResourceIcon,
  QSelectPlaceholder,
  QText,
  QTooltip,
  SearchResource,
  useCurrentUser,
  useIsTruncated,
} from '@qualio/ui-components';
import { RelatedRecordSearchResult, searchRelatedRecords } from '../../../api/quickSearch.api';
import { useResolveRelatedRecords } from '../../../hooks/useResolveRelatedRecords';

type RelatedRecordOption = {
  /** The QRI — this is what gets stored under `related_records`. */
  value: string;
  /** The record code (e.g. `CAPA-5`) — shown in the chip and as the fallback label. */
  label: string;
  title: string;
  type: string;
  resource?: SearchResource;
};

const humanizeResource = (resource: string) => resource.replace(/-/g, ' ').replace(/^\w/, (char) => char.toUpperCase());

const TruncatingText: React.FC<{ label: string }> = ({ label }) => {
  const { ref, isTruncated } = useIsTruncated<HTMLParagraphElement>();
  const text = (
    <QText ref={ref} fontSize="sm" isTruncated>
      {label}
    </QText>
  );
  return isTruncated ? <QTooltip label={label}>{text}</QTooltip> : text;
};

const toOption = (result: RelatedRecordSearchResult): RelatedRecordOption => {
  const code = result.identifiers.code ?? result.name ?? result.identifiers.id;
  const resource = result.resource as SearchResource;
  return {
    value: result.identifiers.qri,
    label: code,
    title: result.name ?? '',
    type: domainOrResourceMapping[resource]?.label ?? humanizeResource(result.resource),
    resource,
  };
};

/**
 * The QAsyncMultiSelect wrapper's public prop types don't surface react-select's
 * `value`/`formatOptionLabel`/`filterOption`, but it forwards unknown props straight to the
 * underlying async select at runtime. This typed view exposes the ones we rely on.
 */
type RelatedRecordsSelectProps = Pick<QAsyncMultiSelectProps<string, string>, 'isClearable'> & {
  value: RelatedRecordOption[];
  loadOptions: (term: string) => Promise<RelatedRecordOption[]>;
  onChange: (selected: RelatedRecordOption[]) => void;
  formatOptionLabel: (option: RelatedRecordOption, meta: { context: 'menu' | 'value' }) => React.ReactNode;
  filterOption: null;
  children?: React.ReactNode;
};

const AsyncMultiSelect = QAsyncMultiSelect as unknown as (props: RelatedRecordsSelectProps) => React.ReactElement;

/**
 * Inline, searchable multi-select for filtering the event list by related records. Searches
 * any related-record resource type (mixable), rendering each result as `code — title` with
 * its type, and stores the selected QRIs as a `string[]` under `related_records` via RHF.
 */
export const RelatedRecordsFilter = () => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();

  const [optionCache, setOptionCache] = useState<Record<string, RelatedRecordOption>>({});
  const currentValue = (useWatch({ name: 'related_records' }) as string[] | undefined) ?? [];

  const qrisToResolve = currentValue.filter((qri) => !optionCache[qri]);
  const { relatedRecords } = useResolveRelatedRecords(qrisToResolve);
  const resolvedByQri = useMemo(() => {
    return relatedRecords.reduce<Record<string, (typeof relatedRecords)[number]>>((acc, record) => {
      acc[record.qri] = record;
      return acc;
    }, {});
  }, [relatedRecords]);

  const selectedOptions = currentValue.map<RelatedRecordOption>((qri) => {
    const cached = optionCache[qri];
    if (cached) {
      return cached;
    }
    const resolved = resolvedByQri[qri];
    return {
      value: qri,
      label: resolved?.code || resolved?.name || '',
      title: resolved?.name ?? '',
      type: '',
      resource: resolved?.resource,
    };
  });

  const loadOptions = useCallback(
    async (term: string): Promise<RelatedRecordOption[]> => {
      if (!term.trim()) {
        return [];
      }
      const results = await searchRelatedRecords(companyId, term);
      return results.map(toOption);
    },
    [companyId],
  );

  const formatOptionLabel = useCallback((option: RelatedRecordOption, { context }: { context: 'menu' | 'value' }) => {
    if (context === 'value') {
      const chip = (
        <QFlex as="span" display="inline-flex" align="center" gap={1}>
          {option.resource && <QResourceIcon domain={option.resource} />}
          <QText as="span">{option.label}</QText>
        </QFlex>
      );
      return option.title ? <QTooltip label={option.title}>{chip}</QTooltip> : chip;
    }
    return (
      <QFlex justify="space-between" align="center" gap={2} w="100%">
        <QFlex align="center" gap={2} minW={0}>
          {option.resource && <QResourceIcon domain={option.resource} />}
          <QBadge flexShrink={0} maxW="7.5rem" boxSizing="border-box">
            {option.label}
          </QBadge>
          {option.title && <TruncatingText label={option.title} />}
        </QFlex>
        <QText fontSize="sm" color="gray.500" flexShrink={0}>
          {option.type}
        </QText>
      </QFlex>
    );
  }, []);

  return (
    <QFormControl label={t('common.labels.relatedRecords')} data-cy="related-records-filter" w="616px">
      <Controller
        name="related_records"
        render={({ field: { onChange } }) => (
          <AsyncMultiSelect
            value={selectedOptions}
            loadOptions={loadOptions}
            formatOptionLabel={formatOptionLabel}
            filterOption={null}
            isClearable={true}
            onChange={(selected) => {
              setOptionCache((prev) => {
                const next = { ...prev };
                selected.forEach((option) => {
                  next[option.value] = option;
                });
                return next;
              });
              onChange(selected.map((option) => option.value));
            }}
          >
            <QSelectPlaceholder>
              <QText>
                {selectedOptions.length ? t('common.filters.searchMore') : t('common.filters.searchRelatedRecords')}
              </QText>
            </QSelectPlaceholder>
          </AsyncMultiSelect>
        )}
      />
    </QFormControl>
  );
};

export default RelatedRecordsFilter;
